import { Link } from "react-router-dom";
import { motion } from "motion/react";
import { HeroFull } from '@/components/HeroFull';
import { HERO_IMAGE_DEFAULT } from '@/config/images';
import Header from "../../components/feature/Header";
import Breadcrumbs from "../../components/feature/Breadcrumbs";
import Footer from "../../components/feature/Footer";
import WhatsAppFloating from "../../components/feature/WhatsAppFloating";
import CookieBanner from "../../components/feature/CookieBanner";
import QuoteFormSection from "../home/components/QuoteFormSection";
import SocialProofSection from "../../components/feature/SocialProofSection";
import SEO from "../../components/feature/SEO";

const services = [
  {
    title: "Toldos motorizados",
    desc: "Controla tu toldo desde el sofá con un botón. Motor Somfy y garantía 5 años.",
    to: "/toldos/toldos-motorizados",
    icon: "ri-remote-control-line",
    rotation: "-rotate-[0.5deg]",
    linkText: "Instalar toldo motorizado →",
  },
  {
    title: "Toldos cofre",
    desc: "Lona protegida dentro de caja de aluminio. Mayor durabilidad y diseño integrado.",
    to: "/toldos/toldos-cofre",
    icon: "ri-archive-2-line",
    rotation: "rotate-[0.3deg]",
    linkText: "Ver toldo cofre →",
  },
  {
    title: "Toldos extensibles",
    desc: "Brazos articulados para terrazas y porches. Desde 3x2m hasta dimensiones XL.",
    to: "/toldos/toldos-extensibles",
    icon: "ri-expand-horizontal-line",
    rotation: "-rotate-[0.3deg]",
    linkText: "Ver toldo extensible →",
  },
  {
    title: "Toldos para veranda",
    desc: "Toldos verticales y de péndulo para cerrar porches y verandas. Protección total.",
    to: "/toldos/toldos-veranda",
    icon: "ri-home-gear-line",
    rotation: "rotate-[0.5deg]",
    linkText: "Toldo para veranda →",
  },
  {
    title: "Toldos de balcón",
    desc: "Solución compacta y elegante para balcones de pisos. Instalación sin obras.",
    to: "/toldos/toldos-balcon",
    icon: "ri-building-4-line",
    rotation: "-rotate-[0.5deg]",
    linkText: "Toldo de balcón →",
  },
  {
    title: "Reparación de toldos",
    desc: "Lona rota, motor estropeado o estructura dañada. Te visitamos en 24h.",
    to: "/toldos/reparacion-toldos",
    icon: "ri-tools-line",
    rotation: "rotate-[0.3deg]",
    linkText: "Reparar mi toldo →",
  },
];

const motivos = [
  { emoji: "☀️", title: "Más de 300 días de sol al año", desc: "Málaga tiene uno de los mejores climas de Europa. Un toldo te permite aprovechar tu terraza sin quemarte." },
  { emoji: "🌡️", title: "Reduce hasta 8°C la temperatura interior", desc: "Un toldo bien colocado bloquea el sol directo y reduce drásticamente el calor que entra en casa." },
  { emoji: "💡", title: "Ahorra hasta 30% en aire acondicionado", desc: "Con menos calor entrando, tu aire acondicionado trabaja menos. Ahorro real en la factura eléctrica." },
  { emoji: "🏡", title: "Aumenta el valor de tu propiedad", desc: "Una terraza habitable con toldo de calidad aumenta el valor y la rentabilidad de tu vivienda." },
];

const precios = [
  { tipo: "Toldo balcón básico", precio: "350 €", emoji: "🏠" },
  { tipo: "Toldo cofre 3x2m", precio: "850 €", emoji: "📦" },
  { tipo: "Toldo motorizado 4x3m", precio: "1.200 €", emoji: "⚡" },
  { tipo: "Pérgola motorizada", precio: "desde 2.500 €", emoji: "🏗️" },
];

const zonas = [
  { name: "Málaga", slug: "malaga" },
  { name: "Marbella", slug: "marbella" },
  { name: "Fuengirola", slug: "fuengirola" },
  { name: "Torremolinos", slug: "torremolinos" },
  { name: "Benalmádena", slug: "benalmadena" },
  { name: "Mijas", slug: "mijas" },
  { name: "Estepona", slug: "estepona" },
];

export default function Toldos() {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        ogType="website"
        breadcrumbs={[{ name: "Inicio", path: "/" }, { name: "Toldos" }]}
      />
      <Header />
      <Breadcrumbs />
      <main>
        <HeroFull
          imageSrc={HERO_IMAGE_DEFAULT}
          imageAlt="Instalación profesional de toldos en Málaga y Costa del Sol"
          badge="⚡ 24h · Respuesta en 20 min · Málaga"
          title="Toldos en Málaga: instalación profesional en 7 días"
          subtitle="Toldos motorizados, cofre y extensibles. Motores Somfy con 5 años de garantía."
          primaryCta={{ href: "tel:+34951120770", label: "Llamar: 951 12 07 70" }}
          secondaryCta={{ href: "#presupuesto", label: "Pedir presupuesto" }}
          trustSignals={["+10 años · desde 2015", "Seguro RC", "Garantía 2 años", "Atendemos toda la Costa del Sol"]}
        />

        <SocialProofSection />

        {/* SERVICIOS GRID */}
        <section className="bg-surface-alt py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">Nuestros servicios de toldos</h2>
              <div className="w-10 h-1 bg-primary mx-auto rounded-full"></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((s, i) => (
                <motion.div
                  key={s.title}
                  className={`bg-white rounded-[20px] p-8 ${s.rotation} border-t-[6px] border-primary shadow-sm hover:shadow-md transition-shadow`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                >
                  <div className="w-12 h-12 bg-primary/10 text-primary rounded-xl flex items-center justify-center mb-5">
                    <i className={`${s.icon} text-2xl`}></i>
                  </div>
                  <h3 className="font-heading font-bold text-xl text-text-primary mb-2">{s.title}</h3>
                  <p className="font-body text-sm text-text-secondary mb-4 leading-relaxed">{s.desc}</p>
                  <Link to={s.to} className="inline-flex items-center gap-1 font-body font-semibold text-sm text-primary hover:text-primary-dark underline underline-offset-4 transition-colors">
                    {s.linkText}
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ¿POR QUÉ UN TOLDO? */}
        <section className="bg-white py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">¿Por qué un toldo en Málaga?</h2>
              <div className="w-10 h-1 bg-primary mx-auto rounded-full"></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {motivos.map((m, i) => (
                <motion.div
                  key={m.title}
                  className="bg-surface-alt rounded-[20px] p-8 text-center shadow-sm hover:shadow-md transition-shadow"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                >
                  <div className="text-4xl mb-4">{m.emoji}</div>
                  <h3 className="font-heading font-bold text-lg text-text-primary mb-2">{m.title}</h3>
                  <p className="font-body text-sm text-text-secondary leading-relaxed">{m.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* MARCAS */}
        <section className="bg-text-primary py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6 text-center">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">Trabajamos con las mejores marcas</h2>
            <p className="font-body text-lg text-slate-300 mb-10 max-w-2xl mx-auto">
              Solo usamos materiales de primeras marcas europeas para que tu toldo dure años.
            </p>
            <div className="flex flex-wrap justify-center gap-6 md:gap-10">
              {["Markilux", "Llaza", "Gaviota Simbac", "Tempotest", "Somfy"].map((brand) => (
                <span key={brand} className="font-heading font-bold text-xl md:text-2xl text-slate-400 tracking-wider">
                  {brand}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* PRECIOS */}
        <section className="bg-surface-alt py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">Precios orientativos</h2>
              <div className="w-10 h-1 bg-primary mx-auto rounded-full"></div>
              <p className="font-body text-lg text-text-secondary mt-4 max-w-2xl mx-auto">
                Estos precios incluyen estructura, lona de calidad, instalación y garantía. Cada toldo se fabrica a medida.
              </p>
            </div>
            <div className="max-w-2xl mx-auto bg-white rounded-3xl overflow-hidden shadow-sm">
              {precios.map((p, i) => (
                <div
                  key={p.tipo}
                  className={`flex items-center justify-between px-6 md:px-8 py-5 ${i !== precios.length - 1 ? "border-b border-border" : ""}`}
                >
                  <span className="flex items-center gap-3 font-body text-base md:text-lg text-text-primary">
                    <span>{p.emoji}</span> {p.tipo}
                  </span>
                  <span className="font-heading font-bold text-lg md:text-xl text-primary">{p.precio}</span>
                </div>
              ))}
            </div>
            <p className="text-center font-body text-sm text-text-secondary mt-6">
              Precios orientativos según dimensiones y acabados. Presupuesto exacto sin compromiso tras visita técnica.
            </p>
          </div>
        </section>

        {/* ZONAS */}
        <section className="bg-white py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">Trabajamos en toda la Costa del Sol</h2>
              <p className="font-body text-lg text-text-secondary max-w-2xl mx-auto">
                Instalamos toldos en Málaga capital y en todos estos municipios. Te visitamos en menos de 24h.
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-3 md:gap-4">
              {zonas.map((z) => (
                <Link
                  key={z.slug}
                  to={`/zonas/${z.slug}`}
                  className="inline-flex items-center px-6 py-3 bg-surface-alt border border-border rounded-pill font-body font-semibold text-base text-text-primary hover:bg-primary hover:text-white hover:border-primary transition-all"
                >
                  {z.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* TRUST IMAGE */}
        <section className="bg-white py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6 text-center">
            <div className="max-w-2xl mx-auto">
              <img
                src="https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=600&q=80&auto=format&fit=crop"
                alt="Técnico profesional instalando toldos en Málaga"
                className="w-full h-auto rounded-xl object-cover aspect-[16/9]"
                loading="lazy"
                width="600"
                height="338"
              />
            </div>
          </div>
        </section>

        {/* TRUST BLOCK */}
        <section className="bg-surface-alt py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { emoji: "🏆", title: "+10 años · desde 2015", desc: "Instalando toldos en Málaga y la Costa del Sol desde 2015." },
                { emoji: "🔧", title: "Técnicos propios", desc: "Sin subcontratas. Nuestro equipo instala y repara directamente." },
                { emoji: "🛡️", title: "Seguro RC", desc: "Seguro de Responsabilidad Civil para tu total tranquilidad." },
                { emoji: "✅", title: "Garantía 2 años", desc: "En lonas, estructuras y mano de obra. Motores Somfy: 5 años." },
              ].map((t, i) => (
                <motion.div
                  key={t.title}
                  className="bg-white rounded-[20px] p-8 shadow-sm"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                >
                  <div className="text-3xl mb-3">{t.emoji}</div>
                  <h3 className="font-heading font-bold text-xl text-text-primary mb-2">{t.title}</h3>
                  <p className="font-body text-sm text-text-secondary leading-relaxed">{t.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== SECCIÓN VIDEODIAGNÓSTICO — 2 COL: texto IZQ, imagen DER, fondo amarillo ===== */}
        <section className="py-16 md:py-24" style={{ backgroundColor: "#ECFCCB" }}>
          <div className="max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 items-center">
              {/* COLUMNA IZQUIERDA — Texto */}
              <motion.div
                className="flex flex-col gap-6 md:order-1"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <p className="font-body font-bold text-xs tracking-widest uppercase" style={{ color: "#D97706" }}>
                  📹 NOVEDAD
                </p>
                <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary leading-tight">
                  Diagnóstico GRATIS por videollamada WhatsApp
                </h2>
                <p className="font-body text-lg text-text-secondary leading-relaxed">
                  ¿No estás seguro de qué toldo necesitas? Muéstranos tu terraza o porche por videollamada y te asesoramos <strong>GRATIS</strong>. Sin compromiso, sin desplazamiento, en menos de 20 minutos.
                </p>
                <ul className="flex flex-col gap-3">
                  {["Diagnóstico instantáneo", "Presupuesto orientativo al momento", "Ahorra la visita técnica si es sencillo", "Disponible L-V 9-19h, S 9-14h"].map((b) => (
                    <li key={b} className="flex items-center gap-3 font-body text-sm text-text-primary">
                      <span className="w-5 h-5 shrink-0 flex items-center justify-center rounded-full bg-primary/10 text-primary font-bold text-xs" aria-hidden="true">✓</span>
                      {b}
                    </li>
                  ))}
                </ul>
                <a
                  href="https://wa.me/34627213347?text=Hola%2C%20quiero%20un%20videodiagn%C3%B3stico%20de%20toldo"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-whatsapp hover:bg-[#128C7E] text-white font-body font-bold text-xl px-8 py-5 rounded-pill transition-colors whitespace-nowrap w-fit"
                >
                  <span aria-hidden="true">📹</span> Iniciar videollamada
                </a>
              </motion.div>
              {/* COLUMNA DERECHA — Imagen */}
              <motion.div
                className="md:order-2"
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <img
                  src="https://readdy.ai/api/search-image?query=A%20real%20professional%20awning%20technician%20showing%20a%20damaged%20retractable%20canvas%20awning%20mechanism%20to%20a%20client%20through%20a%20WhatsApp%20video%20call%20on%20a%20smartphone%20screen%2C%20Mediterranean%20terrace%20visible%20in%20the%20background%2C%20warm%20natural%20daylight%2C%20authentic%20professional%20service%20photography%2C%20detailed%20tools%20and%20hands%20visible%2C%20shallow%20depth%20of%20field&width=600&height=500&seq=toldos-hub-videocall&orientation=landscape&format=webp"
                  alt="Técnico de toldos en videollamada WhatsApp gratis para diagnóstico"
                  loading="lazy"
                  className="w-full h-auto object-cover rounded-2xl"
                  style={{ boxShadow: "0 8px 24px rgba(0,0,0,0.1)" }}
                  width="600"
                  height="500"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* FORM */}
        <div id="presupuesto">
          <QuoteFormSection />
        </div>
      </main>
      <Footer />
      <WhatsAppFloating />
      <CookieBanner />
    </div>
  );
}